import { FC } from 'react';
import { Report as ReportIcon } from '@mui/icons-material';
import { Box, Chip, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { ConfirmationButton, ConfirmationModal } from '@components';
import { useTrans } from '@libs/i18n';

export type ClassificationIssueReason = 'mismatch' | 'failed' | 'unclassified';

export interface ClassificationIssue {
  fileName: string;
  reason: ClassificationIssueReason;
}

interface ClassificationWarningModalProps {
  open: boolean;
  issues: ClassificationIssue[];
  onSaveAnyway: () => void;
  onCancel: () => void;
}

const chipColorByReason: Record<ClassificationIssueReason, 'warning' | 'error' | 'info'> = {
  mismatch: 'warning',
  failed: 'error',
  unclassified: 'info',
};

/**
 * Modal that warns users about document classification issues before saving a domain.
 * Triggered when any uploaded document has a domain mismatch, failed classification,
 * or hasn't been classified yet.
 *
 * Offers two actions:
 * 1. Save Anyway — proceed with saving despite the issues
 * 2. Cancel — dismiss the modal so the user can review documents
 */
export const ClassificationWarningModal: FC<ClassificationWarningModalProps> = observer(
  ({ open, issues, onSaveAnyway, onCancel }) => {
    const { t } = useTrans();

    const mismatchIssues = issues.filter((i) => i.reason === 'mismatch');
    const failedIssues = issues.filter((i) => i.reason === 'failed');
    const unclassifiedIssues = issues.filter((i) => i.reason === 'unclassified');

    const buildSummaryText = () => {
      const parts: string[] = [];
      if (mismatchIssues.length > 0)
        parts.push(t('app.assessment.classification.warning.reason.mismatch', { count: mismatchIssues.length }));
      if (failedIssues.length > 0)
        parts.push(t('app.assessment.classification.warning.reason.failed', { count: failedIssues.length }));
      if (unclassifiedIssues.length > 0)
        parts.push(t('app.assessment.classification.warning.reason.unclassified', { count: unclassifiedIssues.length }));
      return parts.join(', ');
    };

    const buttons: ConfirmationButton[] = [
      {
        label: t('app.assessment.classification.warning.cancel'),
        onClick: onCancel,
        variant: 'outlined',
      },
      {
        label: t('app.assessment.classification.warning.saveAnyway'),
        onClick: onSaveAnyway,
        variant: 'contained',
        customColor: {
          backgroundColor: '#000000',
          color: '#FFFFFF',
          hoverBackgroundColor: '#333333',
        },
        sx: { boxShadow: 'none' },
      },
    ];

    return (
      <ConfirmationModal
        open={open}
        onClose={onCancel}
        title={t('app.assessment.classification.warning.title')}
        icon={<ReportIcon sx={{ fontSize: 22 }} />}
        iconColor="warning"
        buttons={buttons}
        content={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Summary */}
            <Typography variant="body2" color="text.secondary">
              {t('app.assessment.classification.warning.summary', { parts: buildSummaryText() })}
            </Typography>

            {/* Per-document list */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {issues.map((issue, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    px: 1.5,
                    py: 1,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                  }}
                >
                  <Typography variant="body2" sx={{ flex: 1 }} noWrap title={issue.fileName}>
                    {issue.fileName}
                  </Typography>
                  <Chip
                    label={t(`app.assessment.classification.warning.reason.${issue.reason}.label`)}
                    color={chipColorByReason[issue.reason]}
                    size="small"
                    variant="outlined"
                    sx={{ flexShrink: 0, fontWeight: 600 }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        }
      />
    );
  },
);
